import { useState, useRef, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Rating from '@mui/material/Rating';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ForumIcon from '@mui/icons-material/Forum';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import dayjs from 'dayjs';

import { BreadcrumbHeader, DetailPanel, StatusBadge, EmptyState, FulfillmentSearchModal, CreateShipmentModal, SupplierRFQModal, SupplierQuotesModal, ConfirmModal } from '../../components/shared';
import { useAuth } from '../../auth';
import { partOrders, parts, stockLevels, serialItems, purchaseOrders, suppliers, warehouses, customers, partOrderMessages, supplierQuotes, supplierRatings } from '../../services/dataClient';
import { calculateDistance, estimateShippingCost, estimateDeliveryDays } from '../../utils/distance';

// ─── Contract Hub Modal ────────────────────────────────────────────────────────
import ContentCard from "../../components/shared/ContentCard";function ContractHubModal({ open, serialNumber, ticketNumber, partNumber, onClose, onConfirm }) {
  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {if (reason !== 'backdropClick') onClose();}}
      maxWidth="sm"
      fullWidth>
      
      <DialogTitle sx={{ fontWeight: 600,
        fontSize: '1rem',
        color: 'var(--grey-900)',
        pb: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        Update Contract Hub
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '12px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)', lineHeight: 1.7 }}>
          This will push serial number <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--grey-900)' }}>{serialNumber}</Box> to Contract Hub, linking it to ticket <Box component="span" sx={{ fontWeight: 600, color: 'var(--grey-900)' }}>{ticketNumber}</Box> and associating it with the contracted asset record for <Box component="span" sx={{ fontWeight: 600, color: 'var(--grey-900)' }}>{partNumber}</Box>.
        </Typography>
        <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)' }}>
          Sales Ops will be notified.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Typography
          onClick={onClose}
          sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
          
          Cancel
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={onConfirm}
          sx={{ backgroundColor: '#009688',
            '&:hover': { backgroundColor: '#00796B' },
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            boxShadow: 'none',
            px: 2
          }}>
          
          Update Contract Hub
        </Button>
      </DialogActions>
    </Dialog>);

}

// ─── Supplier Review Modal ────────────────────────────────────────────────────
function SupplierReviewModal({ open, supplierName, ticketNumber, ratedBy, onClose, onSubmit }) {
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [touched, setTouched] = useState(false);

  const handleClose = () => {
    setRating(0);
    setReview('');
    setTouched(false);
    onClose();
  };

  const handleSubmit = () => {
    setTouched(true);
    if (rating === 0) return;
    onSubmit({ rating, review: review.trim() });
    setRating(0);
    setReview('');
    setTouched(false);
  };

  return (
    <Dialog open={open} onClose={(e, reason) => {if (reason !== 'backdropClick') handleClose();}} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)' }}>
            Rate Supplier
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px' }}>
            {supplierName} · {ticketNumber}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1, mt: '-2px' }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '12px !important' }}>
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--grey-700)', mb: '8px' }}>
          Overall Rating <Typography component="span" sx={{ color: '#d32f2f' }}>*</Typography>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: touched && rating === 0 ? '4px' : '20px' }}>
          <Rating
            value={rating}
            onChange={(_, v) => setRating(v ?? 0)}
            size="large"
            sx={{ color: '#f59e0b', '& .MuiRating-iconEmpty': { color: 'var(--grey-300)' } }} />
          
          {rating > 0 &&
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-600)' }}>
              {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][rating]}
            </Typography>
          }
        </Box>
        {touched && rating === 0 &&
        <Typography sx={{ fontSize: '0.75rem', color: '#d32f2f', mb: '16px' }}>
            Please select a rating before submitting.
          </Typography>
        }

        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--grey-700)', mb: '8px' }}>
          Review <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--grey-400)' }}>(optional)</Typography>
        </Typography>
        <TextField
          multiline
          minRows={3}
          maxRows={6}
          fullWidth
          placeholder="Describe the supplier's performance — pricing, response time, delivery accuracy…"
          value={review}
          onChange={(e) => setReview(e.target.value)}
          sx={{ '& .MuiOutlinedInput-root': {
              fontSize: '0.875rem',
              borderRadius: '8px',
              '& fieldset': { borderColor: 'var(--grey-200)' },
              '&:hover fieldset': { borderColor: 'var(--grey-400)' },
              '&.Mui-focused fieldset': { borderColor: 'var(--secondary-main)' }
            }
          }} />
        
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        <Typography
          onClick={handleClose}
          sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', mr: 'auto', '&:hover': { color: 'var(--grey-700)' } }}>
          
          Cancel
        </Typography>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{ textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            borderRadius: '8px',
            boxShadow: 'none',
            backgroundColor: '#009688',
            '&:hover': { backgroundColor: '#00796B', boxShadow: 'none' }
          }}>
          
          Submit & Mark Complete
        </Button>
      </DialogActions>
    </Dialog>);

}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function buildTimeline(order, overrideStatus) {
  const status = overrideStatus ?? order.status;
  const sla = dayjs(order.slaDueDate);
  const events = [
  { label: 'Order Created', date: sla.subtract(8, 'day').format('MMM D, YYYY'), active: true }];

  if (['Fulfilled', 'Shipped', 'Completed'].includes(status)) {
    events.push({ label: 'Fulfilled', date: sla.subtract(5, 'day').format('MMM D, YYYY'), active: true });
  }
  if (['Shipped', 'Completed'].includes(status)) {
    events.push({ label: 'Shipped', date: sla.subtract(2, 'day').format('MMM D, YYYY'), active: true });
  }
  if (status === 'Completed') {
    events.push({ label: 'Completed', date: sla.format('MMM D, YYYY'), active: true });
  }
  return events;
}

// ─── Fulfillment Decision card ────────────────────────────────────────────────
const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0);

function FulfillmentDecisionCard({ order, onOpenSearch, onCreateShipment, onOpenCreatePO, onMarkAsUsed, onMarkComplete, linkedPO, onOpenQuotes, quotesLabel }) {
  const navigate = useNavigate();
  const { status, fulfillmentMethod: method, locationName } = order;

  // Compute distance/cost/delivery for stock-based sources
  let distanceMiles = order.distanceMiles ?? null;
  let estShippingCost = order.estShippingCost ?? null;
  let estDeliveryDays = order.estDeliveryDays ?? null;
  if ((method === 'FSL' || method === 'Warehouse' || method === 'OnSite') && locationName && distanceMiles == null && order.customerLat != null) {
    const sl = stockLevels.find((s) => s.locationName === locationName && s.partNumber === order.partNumber);
    if (sl) {
      distanceMiles = calculateDistance(sl.lat, sl.lon, order.customerLat, order.customerLon);
      estShippingCost = estimateShippingCost(distanceMiles, sl.shippingCostPerMile);
      estDeliveryDays = estimateDeliveryDays(distanceMiles, sl.locationType);
    }
  }

  const cardSx = { backgroundColor: 'var(--paper)', border: '1px solid var(--grey-200)', borderRadius: '8px', p: 3 };
  const primaryBtnSx = {
    alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none',
    backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' }
  };
  const changeLink =
  <Typography
    onClick={onOpenSearch}
    sx={{ fontSize: '0.8125rem', color: '#009688', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', '&:hover': { textDecoration: 'underline' } }}>
    
      <EditIcon sx={{ fontSize: 13 }} /> Change Fulfillment Source
    </Typography>;

  // Source detail block — used in Fulfilled / Shipped / Completed
  const sourceBlock = (() => {
    if (method === 'FSL' || method === 'Warehouse' || method === 'OnSite') {
      const sl = stockLevels.find((s) => s.locationName === locationName && s.partNumber === order.partNumber);
      const partRec = parts.find((p) => p.partNumber === order.partNumber);
      const isOnSiteMethod = method === 'OnSite';
      return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <CheckCircleIcon sx={{ color: '#2E7D32', mt: '2px', fontSize: 20 }} />
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#2E7D32' }}>
              {isOnSiteMethod ? 'Used on-site' : 'Fulfilled from stock'}
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px' }}>
              {locationName ?? `${method} location`}
              {sl ? ` — ${sl.quantityOnHand} units on hand` : ''}
            </Typography>
            {isOnSiteMethod ?
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px' }}>
                Same day · No shipping required
              </Typography> :
            distanceMiles != null &&
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px' }}>
                {Math.round(distanceMiles).toLocaleString()} mi · {fmt(estShippingCost)} est. shipping · {estDeliveryDays}
              </Typography>
            }
            {partRec &&
            <Typography
              onClick={() => navigate(`/inventory/search/${partRec.id}`)}
              sx={{ fontSize: '0.8125rem', color: '#009688', cursor: 'pointer', mt: '4px', display: 'inline-flex', alignItems: 'center', gap: '4px', '&:hover': { textDecoration: 'underline' } }}>
              
                <ArrowForwardIcon sx={{ fontSize: 13 }} /> View in Inventory
              </Typography>
            }
          </Box>
        </Box>);

    }
    if (method === 'Supplier') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <StorefrontIcon sx={{ color: 'var(--grey-500)', mt: '2px', fontSize: 20 }} />
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--grey-700)' }}>Supplier</Typography>
          </Box>
        </Box>);

    }
    return null;
  })();

  // Linked PO section (shown for VendorDirect when a PO exists)
  const linkedPOBlock = linkedPO ?
  <Box sx={{ border: '1px solid var(--grey-200)', borderRadius: '6px', p: '12px' }}>
      <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem', color: 'var(--grey-700)', mb: '8px' }}>
        Linked Purchase Order
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
          onClick={() => navigate('/accounting')}
          sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
          
            {linkedPO.poNumber}
          </Typography>
          <StatusBadge status={linkedPO.status} />
        </Box>
        <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)' }}>
          {linkedPO.vendorName}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--grey-800)' }}>
            {fmt(linkedPO.totalCost)}
          </Typography>
          {linkedPO.expectedDeliveryDate &&
        <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)' }}>
              Est. delivery: {linkedPO.expectedDeliveryDate}
            </Typography>
        }
        </Box>
      </Box>
    </Box> :
  null;

  // Amber "No PO linked" warning (edge case — VendorDirect without a linked PO)
  const noPOBlock =
  <Box sx={{ backgroundColor: '#FFF8E1', border: '1px solid #FFE082', borderRadius: '6px', p: '14px' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
        <WarningAmberIcon sx={{ color: '#E65100', fontSize: 18, mt: '1px', flexShrink: 0 }} />
        <Box>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#E65100' }}>
            No purchase order linked
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: '#BF360C', mt: '2px' }}>
            A purchase order has not been created for this vendor order.
          </Typography>
        </Box>
      </Box>
      {status === 'Fulfilled' &&
    <Button
      variant="contained"
      size="small"
      onClick={onOpenCreatePO}
      sx={{ ...primaryBtnSx, mt: '12px', fontSize: '0.8125rem' }}>
      
          Create PO
        </Button>
    }
    </Box>;

  const isSupplier = method === 'Supplier';
  const isOnSite = method === 'OnSite';

  // ── Awaiting Quote: RFQ sent, waiting for supplier responses
  if (status === 'Awaiting Quote') {
    return (
      <Box sx={cardSx}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)', mb: 2 }}>Fulfillment Decision</Typography>
        <Divider sx={{ borderColor: 'var(--grey-200)', mb: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <InfoOutlinedIcon sx={{ color: '#F57F17', mt: '2px', fontSize: 20 }} />
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#F57F17' }}>Awaiting supplier quotes</Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px' }}>Quote requests have been sent. Review responses in the Supplier Quotes card below.</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '8px', flexWrap: 'wrap' }}>
            {onOpenQuotes && quotesLabel &&
            <Button
              variant="outlined"
              size="small"
              onClick={onOpenQuotes}
              sx={{ textTransform: 'none', fontWeight: 600,
                fontSize: '0.875rem', borderColor: '#009688', color: '#009688',
                '&:hover': { backgroundColor: '#E0F2F1', borderColor: '#009688' }
              }}>
              
                Supplier Quotes {quotesLabel}
              </Button>
            }
            <Button variant="contained" size="small" onClick={onOpenSearch} sx={primaryBtnSx}>
              Search Stock Instead
            </Button>
          </Box>
        </Box>
      </Box>);

  }

  // ── Open: no source yet — show Fulfill Order button
  if (status === 'Open') {
    return (
      <Box sx={cardSx}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)', mb: 2 }}>Fulfillment Decision</Typography>
        <Divider sx={{ borderColor: 'var(--grey-200)', mb: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <InfoOutlinedIcon sx={{ color: 'var(--grey-400)', mt: '2px', fontSize: 20 }} />
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--grey-700)' }}>Awaiting fulfillment decision</Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px' }}>No fulfillment source has been selected yet.</Typography>
            </Box>
          </Box>
          <Button variant="contained" size="small" onClick={onOpenSearch} sx={primaryBtnSx}>
            Fulfill Order
          </Button>
        </Box>
      </Box>);

  }

  // ── Fulfilled: source selected, no shipment yet
  if (status === 'Fulfilled') {
    return (
      <Box sx={cardSx}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)', mb: 2 }}>Fulfillment Decision</Typography>
        <Divider sx={{ borderColor: 'var(--grey-200)', mb: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {sourceBlock}
          {!isSupplier && changeLink}
          {/* Supplier: show linked PO or "no PO" warning */}
          {isSupplier && (linkedPO ? linkedPOBlock : noPOBlock)}
          {/* On-Site: Mark as Used button — no shipment needed */}
          {isOnSite &&
          <Button variant="contained" size="small" onClick={onMarkAsUsed} sx={primaryBtnSx}>
              Mark as Used
            </Button>
          }
          {/* FSL / Warehouse / Supplier-with-PO: amber shipment nudge */}
          {!isOnSite && (!isSupplier || linkedPO) &&
          <Box sx={{ backgroundColor: '#FFF8E1', border: '1px solid #FFE082', borderRadius: '6px', p: '14px' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <WarningAmberIcon sx={{ color: '#E65100', fontSize: 18, mt: '1px', flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#E65100' }}>
                    Shipment not yet created
                  </Typography>
                  <Typography sx={{ fontSize: '0.8125rem', color: '#BF360C', mt: '2px' }}>
                    Create a shipment to proceed with this order.
                  </Typography>
                </Box>
              </Box>
              <Button
              variant="contained"
              size="small"
              onClick={onCreateShipment}
              sx={{ ...primaryBtnSx, mt: '12px', fontSize: '0.8125rem' }}>
              
                Create Shipment
              </Button>
            </Box>
          }
        </Box>
      </Box>);

  }

  // ── Shipped: source details + linked shipment + Mark as Completed
  if (status === 'Shipped') {
    return (
      <Box sx={cardSx}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)', mb: 2 }}>Fulfillment Decision</Typography>
        <Divider sx={{ borderColor: 'var(--grey-200)', mb: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {sourceBlock}
          {isSupplier && (linkedPO ? linkedPOBlock : noPOBlock)}
          {order.shipmentNumber &&
          <Typography
            onClick={() => navigate(`/shipments/dispatch/${order.shipmentId}`)}
            sx={{ fontSize: '0.8125rem', color: '#009688', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', '&:hover': { textDecoration: 'underline' } }}>
            
              <ArrowForwardIcon sx={{ fontSize: 13 }} /> {order.shipmentNumber}
            </Typography>
          }
          <Divider sx={{ borderColor: 'var(--grey-200)' }} />
          <Box>
            <Button
              variant="contained"
              size="small"
              onClick={onMarkComplete}
              sx={{ ...primaryBtnSx, fontSize: '0.8125rem' }}>
              
              Mark as Completed
            </Button>
            {isSupplier &&
            <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)', mt: '6px' }}>
                You'll be prompted to rate the supplier before completing.
              </Typography>
            }
          </Box>
        </Box>
      </Box>);

  }

  // ── Completed: read-only source details, no actions
  return (
    <Box sx={cardSx}>
      <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)', mb: 2 }}>Fulfillment Decision</Typography>
      <Divider sx={{ borderColor: 'var(--grey-200)', mb: 2 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {sourceBlock}
        {isSupplier && (linkedPO ? linkedPOBlock : noPOBlock)}
      </Box>
    </Box>);

}

// ─── Chat Panel ───────────────────────────────────────────────────────────────
function ChatPanel({ messages, recipient, onSend }) {
  const [inputText, setInputText] = useState('');
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = () => {
    const text = inputText.trim();
    if (!text || !recipient) return;
    onSend(text);
    setInputText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const fmtTime = (iso) => dayjs(iso).format('MMM D, h:mm A');

  return (
    <ContentCard sx={{ overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid var(--grey-200)', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <ForumIcon sx={{ fontSize: 17, color: 'var(--grey-400)' }} />
        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--grey-900)' }}>Messages</Typography>
        {recipient &&
        <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: 'var(--grey-100)', borderRadius: '20px', px: '10px', py: '3px', gap: '4px', ml: '4px' }}>
            {recipient.sourceLabel &&
          <>
                <Typography sx={{ fontSize: '0.75rem', color: '#009688', fontWeight: 600 }}>{recipient.sourceLabel}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)' }}>·</Typography>
              </>
          }
            <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-700)', fontWeight: 500 }}>{recipient.name}</Typography>
          </Box>
        }
      </Box>

      {/* Message area */}
      <Box
        ref={scrollRef}
        sx={{ height: 300,
          overflowY: 'auto',
          px: 2,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { borderRadius: '4px', backgroundColor: 'var(--grey-300)' }
        }}>
        
        {!recipient &&
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)', fontStyle: 'italic', textAlign: 'center', px: 2 }}>
              Fulfill the order to enable messaging.
            </Typography>
          </Box>
        }
        {recipient && messages.length === 0 &&
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)', fontStyle: 'italic', textAlign: 'center', px: 2 }}>
              No messages yet. Start the conversation.
            </Typography>
          </Box>
        }
        {messages.map((msg) => {
          const isMe = msg.sender === 'coordinator';
          return (
            <Box key={msg.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
              <Box
                sx={{ maxWidth: '85%',
                  px: '12px',
                  py: '8px',
                  borderRadius: isMe ? '8px 8px 2px 8px' : '8px 8px 8px 2px',
                  backgroundColor: isMe ? '#009688' : 'var(--grey-100)',
                  color: isMe ? '#fff' : 'var(--grey-900)'
                }}>
                
                <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.45, wordBreak: 'break-word' }}>{msg.text}</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.6875rem', color: 'var(--grey-400)', mt: '3px', px: '2px' }}>{fmtTime(msg.sentAt)}</Typography>
            </Box>);

        })}
      </Box>

      {/* Input row */}
      <Box sx={{ borderTop: '1px solid var(--grey-200)', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} />
        <IconButton
          size="small"
          disabled={!recipient}
          onClick={() => fileInputRef.current?.click()}
          sx={{ flexShrink: 0, ml: '10px', color: 'var(--grey-400)', '&:hover': { color: '#009688', backgroundColor: 'transparent' }, '&.Mui-disabled': { color: 'var(--grey-200)' } }}>
          
          <AttachFileIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <TextField
          size="small"
          placeholder="Type a message…"
          multiline
          maxRows={3}
          fullWidth
          disabled={!recipient}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.8125rem', borderRadius: 0, '& fieldset': { border: 'none' }, '&.Mui-focused fieldset': { border: 'none' } },
            '& .MuiInputBase-input': { py: '10px', px: '4px' }
          }} />
        
        <IconButton
          size="small"
          disabled={!inputText.trim() || !recipient}
          onClick={handleSend}
          sx={{ flexShrink: 0, mr: '10px', backgroundColor: '#009688', color: '#fff', borderRadius: '8px', width: 32, height: 32, '&:hover': { backgroundColor: '#00796B' }, '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-400)' } }}>
          
          <SendIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>
    </ContentCard>);

}

function ActivityTimeline({ order, overrideStatus }) {
  const events = buildTimeline(order, overrideStatus);
  return (
    <ContentCard
      sx={{ p: 3
      }}>
      
      <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)', mb: 2 }}>
        Activity Timeline
      </Typography>
      <Divider sx={{ borderColor: 'var(--grey-200)', mb: 3 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {events.map((ev, i) => {
          const isLast = i === events.length - 1;
          return (
            <Box key={ev.label} sx={{ display: 'flex', gap: '14px' }}>
              {/* Dot + line */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 16, flexShrink: 0 }}>
                <Box
                  sx={{ width: 12, height: 12, borderRadius: '50%', mt: '3px', flexShrink: 0,
                    backgroundColor: ev.active ? '#009688' : 'var(--grey-300)',
                    border: '2px solid',
                    borderColor: ev.active ? '#009688' : 'var(--grey-300)'
                  }} />
                
                {!isLast &&
                <Box sx={{ width: '2px', flex: 1, minHeight: 28, backgroundColor: 'var(--grey-200)', mt: '2px' }} />
                }
              </Box>

              {/* Content */}
              <Box sx={{ pb: isLast ? 0 : 3 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-900)' }}>
                  {ev.label}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mt: '2px' }}>
                  {ev.date}
                </Typography>
              </Box>
            </Box>);

        })}
      </Box>
    </ContentCard>);

}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function PartOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [showBanner, setShowBanner] = useState(false);
  const [bannerMsg, setBannerMsg] = useState('');
  const [localStatus, setLocalStatus] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [rfqModalOpen, setRfqModalOpen] = useState(false);
  const [quotesModalOpen, setQuotesModalOpen] = useState(false);
  const [createPOOpen, setCreatePOOpen] = useState(false);
  const [createShipmentOpen, setCreateShipmentOpen] = useState(false);
  // localFulfillment: { fulfillmentMethod, locationName?, vendorDirectReason?, distanceMiles?, estShippingCost?, estDeliveryDays?, linkedPurchaseOrderId?, linkedPurchaseOrderNumber? } | null
  const [localFulfillment, setLocalFulfillment] = useState(null);
  // localShipment: created in-session before page reload
  const [localShipment, setLocalShipment] = useState(null);
  // localPO: PO created in this session via the Vendor Direct flow
  const [localPO, setLocalPO] = useState(null);
  // Messages — seeded from mock data, updated locally
  const [localMessages, setLocalMessages] = useState(() => partOrderMessages.filter((m) => m.partOrderId === id));
  const [contractHubOpen, setContractHubOpen] = useState(false);

  const order = partOrders.find((o) => o.id === id);
  const part = order ? parts.find((p) => p.partNumber === order.partNumber) : null;

  // Must be declared before any early return to satisfy Rules of Hooks
  const effectiveStatus = localStatus ?? order?.status;
  const effectiveOrder = useMemo(() => {
    if (!order) return null;
    return {
      ...order,
      ...(localFulfillment ?? {}),
      status: effectiveStatus,
      ...(localShipment ? { shipmentId: localShipment.id, shipmentNumber: localShipment.shipmentNumber } : {})
    };
  }, [order, localFulfillment, effectiveStatus, localShipment]);

  // Quotes summary for the trigger button (read-only; modal owns mutations)
  const quotesSummary = useMemo(() => {
    const quotes = supplierQuotes.filter((q) => q.partOrderId === id);
    const received = quotes.filter((q) => q.status !== 'Pending').length;
    return { total: quotes.length, received };
  }, [id]);

  const resolvedRecipient = useMemo(() => {
    if (!effectiveOrder) return null;
    const method = effectiveOrder.fulfillmentMethod;
    if (!method || effectiveStatus === 'Open') return null;
    if (method === 'Supplier') {
      const po = localPO ?? purchaseOrders.find((p) => p.id === effectiveOrder.linkedPurchaseOrderId);
      if (!po) return null;
      const sup = suppliers.find((s) => s.name === po.vendorName);
      return sup ? { type: 'Supplier', name: sup.contactName, email: sup.contactEmail, sourceLabel: po.vendorName } : null;
    }
    const wh = warehouses.find((w) => w.name === effectiveOrder.locationName);
    return wh ? { type: 'StockLocation', name: wh.contactName, email: wh.contactEmail, sourceLabel: effectiveOrder.locationName } : null;
  }, [effectiveOrder, effectiveStatus, localPO]);

  if (!order) {
    return (
      <Box sx={{ p: 3 }}>
        <EmptyState
          title="Order not found"
          description={`No part order with ID "${id}" exists in the system.`} />
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Typography
            onClick={() => navigate('/orders/parts')}
            sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
            
            ← Back to Part Orders
          </Typography>
        </Box>
      </Box>);

  }

  const handleSelectStock = (stockLevel, distanceMiles) => {
    // Auto-assign the first Available serial at this location (null for non-serialized parts)
    const assignedSerial = serialItems.find(
      (si) => si.partNumber === order.partNumber &&
      si.locationName === stockLevel.locationName &&
      si.status === 'Available'
    ) ?? null;
    if (assignedSerial) {
      assignedSerial.status = 'Reserved';
      assignedSerial.ticketNumber = order.ticketNumber;
    }
    setSearchModalOpen(false);
    setLocalFulfillment({
      fulfillmentMethod: stockLevel.locationType === 'On-Site' ? 'OnSite' : stockLevel.locationType,
      locationName: stockLevel.locationName,
      distanceMiles,
      estShippingCost: estimateShippingCost(distanceMiles, stockLevel.shippingCostPerMile),
      estDeliveryDays: estimateDeliveryDays(distanceMiles, stockLevel.locationType),
      serialNumber: assignedSerial?.serialNumber ?? null
    });
    setLocalStatus('Fulfilled');
    setBannerMsg(`Fulfillment source updated to ${stockLevel.locationName}.`);
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 4000);
  };

  const handleMarkAsUsed = () => {
    setLocalStatus('Completed');
    setBannerMsg(`Part marked as used for ${order.ticketNumber}.`);
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 4000);
  };

  const handleMarkComplete = () => {
    if (effectiveOrder.fulfillmentMethod === 'Supplier') {
      setReviewOpen(true);
    } else {
      setConfirmCompleteOpen(true);
    }
  };

  const handleReviewSubmit = ({ rating, review }) => {
    const po = localPO ?? purchaseOrders.find((p) => p.id === effectiveOrder.linkedPurchaseOrderId);
    const supplierName = po?.vendorName ?? 'Unknown Supplier';
    supplierRatings.unshift({
      id: `rtg-${Date.now()}`,
      supplierName,
      quoteId: null,
      ticketNumber: order.ticketNumber,
      rating,
      review: review || '',
      ratedBy: currentUser?.name ?? 'Coordinator',
      ratedAt: dayjs().format('YYYY-MM-DD')
    });
    setReviewOpen(false);
    setLocalStatus('Completed');
    setBannerMsg(`Order completed — supplier review submitted for ${supplierName}.`);
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 5000);
  };

  const handleCreateShipment = (newShipment) => {
    const sn = localFulfillment?.serialNumber ?? order.serialNumber ?? null;
    // Mark serial as Shipped in inventory
    if (sn) {
      const serial = serialItems.find((si) => si.serialNumber === sn);
      if (serial) serial.status = 'Shipped';
    }
    const linked = { ...newShipment, partOrderId: order.id, ticketNumber: order.ticketNumber, serialNumber: sn };
    setLocalShipment(linked);
    setLocalStatus('Shipped');
    setCreateShipmentOpen(false);
    setBannerMsg(`Shipment ${linked.shipmentNumber} created and linked to ${order.ticketNumber}.`);
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 5000);
  };

  const handleRFQSubmit = ({ supplierNames, notes }) => {
    const today = dayjs().format('YYYY-MM-DD');
    // Push new Pending quote records into the module-level array so SupplierQuotesModal picks them up
    supplierNames.forEach((name, i) => {
      supplierQuotes.push({
        id: `sqr-detail-${Date.now()}-${i}`,
        partOrderId: order.id,
        ticketNumber: order.ticketNumber,
        partNumber: order.partNumber,
        sentDate: today,
        supplierName: name,
        supplierEmail: (() => {
          const sup = suppliers.find((s) => s.name === name);
          const partOem = parts.find((p) => p.partNumber === order.partNumber)?.oem ?? null;
          const matchedRep = sup?.salesReps?.find((r) => r.oem === partOem);
          return matchedRep?.email ?? sup?.contactEmail ?? '';
        })(),
        quantityRequested: 1,
        unitCost: null,
        quantityAvailable: null,
        leadTimeDays: null,
        responseDate: null,
        notes: notes || null,
        status: 'Pending'
      });
    });
    setLocalStatus('Awaiting Quote');
    setRfqModalOpen(false);
    const n = supplierNames.length;
    setBannerMsg(`Quote requests sent to ${n} supplier${n !== 1 ? 's' : ''} for ${order.ticketNumber}.`);
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 5000);
  };

  const handleSendMessage = (text) => {
    const msg = {
      id: `msg-local-${Date.now()}`,
      partOrderId: id,
      sender: 'coordinator',
      senderName: 'You',
      text,
      sentAt: new Date().toISOString()
    };
    setLocalMessages((prev) => [...prev, msg]);
  };

  const handleContractHubConfirm = () => {
    setContractHubOpen(false);
    setBannerMsg(`Serial number ${effectiveOrder.serialNumber} sent to Contract Hub. Sales Ops has been notified.`);
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 5000);
  };

  const methodLabel = { FSL: 'FSL', Warehouse: 'Warehouse', Supplier: 'Supplier', OnSite: 'On-Site' };

  const contactRecord = customers.find((c) => c.name === (order.resellerName ?? order.customerName)) ?? null;

  const orderInfoFields = [
  { label: 'Ticket #', value: order.ticketNumber },
  {
    label: 'Part #',
    value: part ?
    <Typography
      component="span"
      onClick={() => navigate(`/inventory/search/${part.id}`)}
      sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
      
          {order.partNumber}
        </Typography> :
    order.partNumber
  },
  { label: 'Description', value: part?.description ?? '—' },
  { label: 'Customer', value: order.customerName },
  { label: 'Contact', value: contactRecord?.contactName ?? '—' },
  { label: 'Contact Phone', value: contactRecord?.contactPhone ?? '—' },
  { label: 'Ship-to Address', value: order.customerAddress ?? '—' },
  { label: 'Created Date', value: order.createdDate },
  { label: 'SLA', value: order.sla }];

  const fulfillmentFields = [
  { label: 'Fulfillment Method', value: methodLabel[effectiveOrder.fulfillmentMethod] ?? effectiveOrder.source ?? '—' },
  ...(effectiveOrder.locationName ?
  [{ label: 'Location', value: effectiveOrder.locationName }] :
  []),
  { label: 'Fulfillment Status', value: effectiveOrder.status },
  ...(effectiveOrder.serialNumber ?
  [{
    label: 'Serial #',
    value:
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{effectiveOrder.serialNumber}</Box>
              {effectiveStatus === 'Completed' &&
      <Button
        variant="outlined"
        size="small"
        onClick={() => setContractHubOpen(true)}
        sx={{ textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.75rem',
          borderColor: '#009688',
          color: '#009688',
          '&:hover': { backgroundColor: '#E0F2F1', borderColor: '#009688' },
          px: 1,
          py: '2px',
          minWidth: 'unset',
          lineHeight: 1.5
        }}>
        
                  Update Contract Hub
                </Button>
      }
            </Box>

  }] :
  []),
  {
    label: 'Linked Shipment',
    value: effectiveOrder.shipmentNumber ?
    <Typography
      component="span"
      onClick={() => navigate(`/shipments/dispatch/${effectiveOrder.shipmentId}`)}
      sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', display: 'inline', '&:hover': { textDecoration: 'underline' } }}>
      
          {effectiveOrder.shipmentNumber}
        </Typography> :
    '—'
  }];

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Success banner */}
      {showBanner &&
      <Box
        sx={{ display: 'flex',
          alignItems: 'center',
          gap: '10px',
          backgroundColor: '#E8F5E9',
          border: '1px solid #A5D6A7',
          borderRadius: '8px',
          px: 2,
          py: '12px'
        }}>
        
          <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 600 }}>
            {bannerMsg}
          </Typography>
        </Box>
      }

      {/* Header */}
      <BreadcrumbHeader
        title={effectiveOrder.ticketNumber}
        subtitle={effectiveOrder.customerName}
        crumbs={[
        { label: 'Orders' },
        { label: 'Part Orders', path: '/orders/parts' },
        { label: effectiveOrder.ticketNumber }]
        } />

      {/* Two-column layout — 60/40 split */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'flex-start' }}>
        {/* Left column — 60% */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 3, minWidth: 0 }}>
          <DetailPanel title="Order Info" fields={orderInfoFields} />
          <DetailPanel title="Fulfillment" fields={fulfillmentFields} />
          {['Fulfilled', 'Shipped', 'Completed'].includes(effectiveStatus) &&
          <ChatPanel messages={localMessages} recipient={resolvedRecipient} onSend={handleSendMessage} />
          }
        </Box>

        {/* Right column — 40% */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 2, minWidth: 0 }}>
          <FulfillmentDecisionCard
            order={effectiveOrder}
            onOpenSearch={() => setSearchModalOpen(true)}
            onCreateShipment={() => setCreateShipmentOpen(true)}
            onOpenCreatePO={() => setCreatePOOpen(true)}
            onMarkAsUsed={handleMarkAsUsed}
            onMarkComplete={handleMarkComplete}
            linkedPO={localPO ?? purchaseOrders.find((p) => p.id === effectiveOrder.linkedPurchaseOrderId) ?? null}
            onOpenQuotes={quotesSummary.total > 0 ? () => setQuotesModalOpen(true) : undefined}
            quotesLabel={quotesSummary.total > 0 ? `${quotesSummary.received}/${quotesSummary.total}` : undefined} />
          
          <ActivityTimeline order={order} overrideStatus={localStatus} />
        </Box>
      </Box>

      {/* Fulfillment search modal */}
      <FulfillmentSearchModal
        open={searchModalOpen || createPOOpen}
        order={order}
        onClose={() => {setSearchModalOpen(false);setCreatePOOpen(false);}}
        onSelectStock={handleSelectStock}
        onRequestSupplierQuote={() => {setSearchModalOpen(false);setCreatePOOpen(false);setRfqModalOpen(true);}} />

      {/* Supplier RFQ modal */}
      <SupplierRFQModal
        open={rfqModalOpen}
        order={order}
        onClose={() => setRfqModalOpen(false)}
        onSubmit={handleRFQSubmit} />

      {/* Supplier Quotes modal */}
      <SupplierQuotesModal
        open={quotesModalOpen}
        order={effectiveOrder}
        onClose={() => setQuotesModalOpen(false)}
        onStatusChange={(newStatus, newPO) => {
          setLocalStatus(newStatus);
          setQuotesModalOpen(false);
          if (newPO) {
            setLocalPO(newPO);
            setLocalFulfillment({
              fulfillmentMethod: 'Supplier',
              locationName: null,
              linkedPurchaseOrderId: newPO.id,
              linkedPurchaseOrderNumber: newPO.poNumber
            });
            setBannerMsg(`Quote accepted from ${newPO.vendorName} — PO ${newPO.poNumber} created.`);
          }
          setShowBanner(true);
          setTimeout(() => setShowBanner(false), 5000);
        }} />

      {/* Supplier review modal — shown when marking supplier-origin orders as Completed */}
      <SupplierReviewModal
        open={reviewOpen}
        supplierName={(() => {
          const po = localPO ?? purchaseOrders.find((p) => p.id === effectiveOrder.linkedPurchaseOrderId);
          return po?.vendorName ?? 'Supplier';
        })()}
        ticketNumber={order.ticketNumber}
        ratedBy={currentUser?.name ?? 'Coordinator'}
        onClose={() => setReviewOpen(false)}
        onSubmit={handleReviewSubmit} />

      {/* Confirm completion — shown for non-supplier orders */}
      <ConfirmModal
        open={confirmCompleteOpen}
        title="Mark as Completed"
        message={`Mark ${effectiveOrder?.ticketNumber} as completed? This will close the order.`}
        confirmLabel="Complete"
        onConfirm={() => {setConfirmCompleteOpen(false);handleMarkAsUsed();}}
        onCancel={() => setConfirmCompleteOpen(false)} />

      {/* Contract Hub modal */}
      <ContractHubModal
        open={contractHubOpen}
        serialNumber={effectiveOrder.serialNumber}
        ticketNumber={order.ticketNumber}
        partNumber={order.partNumber}
        onClose={() => setContractHubOpen(false)}
        onConfirm={handleContractHubConfirm} />

      {/* Create Shipment modal — pre-filled from this order */}
      <CreateShipmentModal
        open={createShipmentOpen}
        onClose={() => setCreateShipmentOpen(false)}
        onSubmit={handleCreateShipment}
        prefill={(() => {
          const addr = effectiveOrder.customerAddress ?? '';
          const parts = addr.split(', ');
          const stateZip = (parts[parts.length - 1] ?? '').split(' ');
          return {
            ticketNumber: effectiveOrder.ticketNumber,
            originType: effectiveOrder.fulfillmentMethod === 'Supplier' ?
            'Supplier' :
            effectiveOrder.fulfillmentMethod ?? '',
            originLocation: effectiveOrder.locationName ?? '',
            customerName: effectiveOrder.customerName,
            customerAddress: parts.length >= 3 ? parts.slice(0, parts.length - 2).join(', ') : addr,
            customerCity: parts.length >= 3 ? parts[parts.length - 2] : '',
            customerState: parts.length >= 3 ? stateZip[0] ?? '' : '',
            customerZip: parts.length >= 3 ? stateZip[1] ?? '' : ''
          };
        })()} />
      
    </Box>);

}